import type { MachineLoadState, Session } from '@orgus/protocol';
import type { ReactNode } from 'react';
import { currentStartOf, ownWorkLine, ownWorkOf } from '@orgus/protocol';
import { LiveNumber, cn, formatClock, formatDuration, formatMoney, useNow } from '@orgus/ui';
import { useSessionCost } from '../costs/useCosts.ts';
import { useExt } from '../../lib/store.ts';
import { MACHINE_LOAD_STATE_EXT } from '../machine-load/model.ts';
import { findWaitingInfo } from '../machine-load/cpuWaiting.ts';
import { WaitingForCpuBadge } from '../machine-load/WaitingForCpuBadge.tsx';
import { compactLowerBound } from './counts.ts';
import { sessionRanMs, sessionTicking } from './duration.ts';
import { tokenTotal } from './labels.ts';

/**
 * The drawer's facts, the header's (project, machine, branch) and the three numbers, share one
 * grid and one cell: the same columns, set apart by thin inset lines with room above and below,
 * so the labels line up from row to row.
 */
export const FACT_GRID = 'inset-dividers-sm grid min-w-0 grid-cols-2 gap-y-4 sm:grid-cols-4';
export const FACT_CELL = 'flex min-w-0 flex-col pr-4 even:pl-4 sm:pl-4 sm:first:pl-0';

/** An empty fact: a quiet dash, never a word in the value's place (placeholders never look like data). */
export function NoValue({ label = 'None' }: { label?: string }) {
  return (
    <span className="text-fg-subtle">
      <span aria-hidden>–</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}

function Cell({ label, children, title }: { label: string; children: ReactNode; title?: string }) {
  return (
    <div className={cn(FACT_CELL, 'gap-1')}>
      <span className="truncate text-caption text-fg-subtle">{label}</span>
      <span className="truncate font-display text-metric-sm text-fg num-display" title={title}>
        {children}
      </span>
    </div>
  );
}

/** How long it has run (or ran): ticking only while it works or waits (sessionTicking). */
function useRan(s: Session): number {
  const now = useNow(sessionTicking(s) ? 1_000 : 60_000);
  return sessionRanMs(s, now);
}

/**
 * The session in three numbers under the drawer's title: how long it has run, its tokens and its
 * cost. Live numbers roll as the session works. The token split, the hourly rate, the folder and
 * the subagents are on Details and Agents. On a phone the drawer says it in one line instead
 * (SessionSummaryLine), so the conversation keeps the screen.
 */
export function SessionFacts({ s }: { s: Session }) {
  const usage = useSessionCost(s.id);
  const total = tokenTotal(s);
  const ran = useRan(s);
  const cost = usage && usage.total.cost > 0 ? usage.total.cost : 0;
  const ticking = sessionTicking(s);
  const since = s.status === 'ended' ? 'Ended' : 'Idle since';
  const start = currentStartOf(s);
  // ORGU-132: woken or relaunched since it first started: the first start is a small fact.
  const first = start > s.startedAt ? `First started ${formatClock(s.startedAt)}` : null;
  return (
    <div className="shrink-0 max-md:hidden">
      <OwnWorkLine s={s} />
      <div className={cn(FACT_GRID, 'mx-6 mb-2 border-t border-border py-4 sm:grid-cols-3')} role="group" aria-label="Session numbers">
        <div className={cn(FACT_CELL, 'gap-1')}>
          <span className="truncate text-caption text-fg-subtle">{ticking ? 'Running for' : 'Ran for'}</span>
          <span className="truncate font-display text-metric-sm text-fg num-display" title={ticking ? `Since ${formatClock(start)}` : `${since} ${formatClock(s.lastActivity)}`}>
            {formatDuration(ran)}
          </span>
          {first && <span className="truncate text-caption text-fg-subtle" data-first-started="">{first}</span>}
        </div>
        <Cell label="Tokens">{total > 0 ? compactLowerBound(total, s.partial) : <NoValue />}</Cell>
        <Cell label="Cost">
          {cost ? <LiveNumber value={cost} format={formatMoney} /> : usage?.unpriced && total > 0 ? <span className="text-fg-subtle">Not priced</span> : <NoValue />}
        </Cell>
      </div>
    </div>
  );
}

/**
 * What a session whose turn ended waits on (ORGU-132): its own tests, agent or monitor, with how
 * long it has run, or Waiting for CPU while its lease waits (ORGU-131). Nothing otherwise.
 */
export function OwnWorkLine({ s }: { s: Session }) {
  const own = ownWorkOf(s);
  const now = useNow(own ? 30_000 : 600_000);
  const cpu = findWaitingInfo(useExt<MachineLoadState>(MACHINE_LOAD_STATE_EXT), { sessionId: s.id, what: s.lastText });
  if (!own) return null;
  return (
    <p className="mx-6 mb-1 truncate text-ui text-fg-muted" data-own-work="">
      {cpu?.isWaiting ? <WaitingForCpuBadge placeInLine={cpu.placeInLine} estimateMinutes={cpu.estimateMinutes} work={cpu.work} /> : ownWorkLine(own, now)}
    </p>
  );
}

/** The phone's header: the session's place and time in one line ("Qawl-Agents on studio · 1h 05m"). */
export function SessionSummaryLine({ s, project, machine, className }: { s: Session; project: string; machine: string; className?: string }) {
  const ran = useRan(s);
  return (
    <p className={cn('truncate text-ui text-fg-muted', className)}>
      {project} <span className="text-fg-subtle">on</span> {machine}
      <span aria-hidden className="text-fg-subtle">
        {' '}
        ·{' '}
      </span>
      <span className="sr-only">, {sessionTicking(s) ? 'running for' : 'ran for'} </span>
      <span className="num">{formatDuration(ran)}</span>
    </p>
  );
}
